import { create } from 'zustand';
import { persist } from 'zustand/middleware';

interface CompanyContextState {
  // User's assigned company (set after login)
  companyId: string | null;
  companyName: string | null;

  // Super-admin override for context switching
  activeCompanyId: string | null;
  activeCompanyName: string | null;

  // Actions
  setCompany: (companyId: string | null, companyName?: string | null) => void;
  setActiveCompany: (companyId: string | null, companyName?: string | null) => void;
  clearActiveCompany: () => void;
  reset: () => void;
}

export const useCompanyContextStore = create<CompanyContextState>()(
  persist(
    (set) => ({
      companyId: null,
      companyName: null,
      activeCompanyId: null,
      activeCompanyName: null,

      setCompany: (companyId, companyName = null) =>
        set({ companyId, companyName }),

      setActiveCompany: (companyId, companyName = null) =>
        set({ activeCompanyId: companyId, activeCompanyName: companyName }),

      clearActiveCompany: () =>
        set({ activeCompanyId: null, activeCompanyName: null }),

      reset: () =>
        set({
          companyId: null,
          companyName: null,
          activeCompanyId: null,
          activeCompanyName: null,
        }),
    }),
    {
      name: 'company-context',
    },
  ),
);

/**
 * Get the effective company ID for API calls.
 * Returns activeCompanyId (super-admin override) if set, otherwise returns user's assigned companyId.
 * Can be used outside React components.
 */
export function getEffectiveCompanyId(): string | null {
  const { companyId, activeCompanyId } = useCompanyContextStore.getState();
  return activeCompanyId ?? companyId;
}

/**
 * Get the effective company name for display.
 * Can be used outside React components.
 */
export function getEffectiveCompanyName(): string | null {
  const { companyName, activeCompanyId, activeCompanyName } = useCompanyContextStore.getState();
  return activeCompanyId ? activeCompanyName : companyName;
}

/**
 * Check if currently viewing as a different company (context switch active).
 */
export function isContextSwitchActive(): boolean {
  const { companyId, activeCompanyId } = useCompanyContextStore.getState();
  return activeCompanyId !== null && activeCompanyId !== companyId;
}
